import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logAuditAction } from '@/hooks/useAuditLog';

export interface TicketRow {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  category: string | null;
  status: string;
  assigned_to: string | null;
  solution_message_id: string | null;
  created_at: string;
  updated_at: string;
  // joined
  user_name?: string;
  user_email?: string;
  user_org_id?: string | null;
  assigned_name?: string;
}

export interface TicketMessageRow {
  id: string;
  ticket_id: string;
  sender_type: string | null;
  sender_id: string | null;
  message: string;
  attachments: any;
  created_at: string;
  sender_name?: string;
}

// ── Participant: own tickets ──
export function useMyTickets() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['my-tickets', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('support_tickets')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as TicketRow[];
    },
    enabled: !!user,
    refetchInterval: 15000,
  });
}

// ── Realtime subscription for participant tickets ──
export function useMyTicketsRealtime() {
  const { user } = useAuth();
  const qc = useQueryClient();

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel('my-tickets-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'support_tickets', filter: `user_id=eq.${user.id}` }, () => {
        qc.invalidateQueries({ queryKey: ['my-tickets'] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, qc]);
}

// ── Admin/SA: all tickets with user info ──
export function useAllTickets(orgFilter?: string) {
  const { currentRole, profile } = useAuth();
  return useQuery({
    queryKey: ['all-tickets', orgFilter, currentRole],
    queryFn: async () => {
      // RLS handles visibility: SA sees all, admin sees own org only
      const { data: tickets, error } = await supabase
        .from('support_tickets')
        .select('*')
        .order('updated_at', { ascending: false });
      if (error) throw error;
      if (!tickets || tickets.length === 0) return [];

      // Get user profiles for these tickets
      const userIds = [...new Set(tickets.map(t => t.user_id))];
      const assignedIds = [...new Set(tickets.map(t => t.assigned_to).filter(Boolean))] as string[];
      const allIds = [...new Set([...userIds, ...assignedIds])];

      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name, email, organization_id')
        .in('id', allIds);

      const profileMap = new Map((profiles ?? []).map(p => [p.id, p]));

      let result: TicketRow[] = tickets.map(t => ({
        ...t,
        status: t.status ?? 'open',
        user_name: profileMap.get(t.user_id)?.full_name ?? '',
        user_email: profileMap.get(t.user_id)?.email ?? '',
        user_org_id: profileMap.get(t.user_id)?.organization_id ?? null,
        assigned_name: t.assigned_to ? (profileMap.get(t.assigned_to)?.full_name ?? '') : undefined,
      }));

      // SA: client-side org filter for UI dropdown
      if (currentRole === 'super_admin' && orgFilter && orgFilter !== 'all') {
        result = result.filter(t => t.user_org_id === orgFilter);
      }

      return result;
    },
    enabled: currentRole === 'super_admin' || currentRole === 'admin',
  });
}

// ── Messages for a ticket ──
export function useTicketMessages(ticketId: string | null) {
  return useQuery({
    queryKey: ['ticket-messages', ticketId],
    queryFn: async () => {
      if (!ticketId) return [];
      const { data, error } = await supabase
        .from('ticket_messages')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });
      if (error) throw error;

      if (!data || data.length === 0) return [];

      // Get sender names
      const senderIds = [...new Set(data.map(m => m.sender_id).filter(Boolean))] as string[];
      const { data: profiles } = senderIds.length > 0
        ? await supabase.from('profiles').select('id, full_name').in('id', senderIds)
        : { data: [] };
      const nameMap = new Map((profiles ?? []).map(p => [p.id, p.full_name]));

      return data.map(m => ({
        ...m,
        sender_name: m.sender_id ? (nameMap.get(m.sender_id) ?? '') : '',
      })) as TicketMessageRow[];
    },
    enabled: !!ticketId,
    refetchInterval: 10000,
  });
}

// ── Realtime subscription for ticket messages ──
export function useTicketMessagesRealtime(ticketId: string | null) {
  const qc = useQueryClient();

  useEffect(() => {
    if (!ticketId) return;
    const channel = supabase
      .channel(`ticket-messages-${ticketId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'ticket_messages', filter: `ticket_id=eq.${ticketId}` }, () => {
        qc.invalidateQueries({ queryKey: ['ticket-messages', ticketId] });
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [ticketId, qc]);
}

// ── Create ticket ──
export function useCreateTicket() {
  const { user } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      title: string;
      description: string;
      category: string;
      firstMessage: string;
      attachmentUrls?: { name: string; url: string; type: string; size: number }[];
    }) => {
      if (!user) throw new Error('Not authenticated');

      console.log('[useCreateTicket] Creating ticket with:', {
        user_id: user.id,
        title: input.title,
        category: input.category,
        hasDescription: !!input.description,
        hasAttachments: (input.attachmentUrls?.length ?? 0) > 0,
      });

      // Insert ticket
      const { data: ticket, error: tErr } = await supabase
        .from('support_tickets')
        .insert({
          user_id: user.id,
          title: input.title,
          description: input.description,
          category: input.category,
        })
        .select()
        .single();
      
      if (tErr) {
        console.error('[useCreateTicket] Ticket insert error:', tErr);
        throw tErr;
      }
      console.log('[useCreateTicket] Ticket created:', ticket.id);

      // Insert first message
      const { error: mErr } = await supabase
        .from('ticket_messages')
        .insert({
          ticket_id: ticket.id,
          sender_type: 'user' as const,
          sender_id: user.id,
          message: input.firstMessage,
          attachments: input.attachmentUrls && input.attachmentUrls.length > 0
            ? input.attachmentUrls
            : null,
        });
      if (mErr) {
        console.error('[useCreateTicket] Message insert error:', mErr);
        throw mErr;
      }
      console.log('[useCreateTicket] First message created for ticket:', ticket.id);

      return ticket;
    },
    onSuccess: (ticket) => {
      qc.invalidateQueries({ queryKey: ['my-tickets'] });
      qc.invalidateQueries({ queryKey: ['all-tickets'] });
      if (user) {
        logAuditAction(user.id, 'ticket_create', `Ticket creado: ${ticket.title}`, { ticket_id: ticket.id } as any);
      }
      // Aviso por correo a soporte; nunca bloquea la creación del ticket.
      supabase.functions
        .invoke('notify-support-ticket', { body: { ticket_id: ticket.id } })
        .then(({ error }) => { if (error) console.error('[notify-support-ticket]', error); })
        .catch((e) => console.error('[notify-support-ticket]', e));
    },
  });
}

// ── Send message ──
export function useSendMessage() {
  const { user } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      ticketId: string;
      message: string;
      senderType: 'user' | 'agent';
      attachmentUrls?: { name: string; url: string; type: string; size: number }[];
    }) => {
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('ticket_messages')
        .insert({
          ticket_id: input.ticketId,
          sender_type: input.senderType,
          sender_id: user.id,
          message: input.message,
          attachments: input.attachmentUrls && input.attachmentUrls.length > 0
            ? input.attachmentUrls
            : null,
        })
        .select()
        .single();
      if (error) throw error;

      // Update ticket updated_at and status if first support reply
      if (input.senderType === 'agent') {
        await supabase
          .from('support_tickets')
          .update({ updated_at: new Date().toISOString(), status: 'in_progress' as const })
          .eq('id', input.ticketId)
          .eq('status', 'open');
      }
      // Always bump updated_at
      await supabase
        .from('support_tickets')
        .update({ updated_at: new Date().toISOString() })
        .eq('id', input.ticketId);

      return data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['ticket-messages', vars.ticketId] });
      qc.invalidateQueries({ queryKey: ['my-tickets'] });
      qc.invalidateQueries({ queryKey: ['all-tickets'] });
    },
  });
}

// ── Update ticket status ──
export function useUpdateTicketStatus() {
  const { user } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, status }: { ticketId: string; status: string }) => {
      const { error } = await supabase
        .from('support_tickets')
        .update({ status: status as any, updated_at: new Date().toISOString() })
        .eq('id', ticketId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-tickets'] });
      qc.invalidateQueries({ queryKey: ['all-tickets'] });
      if (user) {
        logAuditAction(user.id, 'ticket_status_update', 'Estado de ticket actualizado');
      }
    },
  });
}

// ── Assign agent ──
export function useAssignTicket() {
  const { user } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, agentId }: { ticketId: string; agentId: string | null }) => {
      const { error } = await supabase
        .from('support_tickets')
        .update({ assigned_to: agentId, updated_at: new Date().toISOString() })
        .eq('id', ticketId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['all-tickets'] });
      if (user) {
        logAuditAction(user.id, 'ticket_assign', 'Agente asignado a ticket');
      }
    },
  });
}

// ── Upload attachment to storage ──
export async function uploadTicketAttachment(
  file: File,
  userId: string
): Promise<{ name: string; url: string; type: string; size: number }> {
  const ext = file.name.split('.').pop();
  const path = `${userId}/tickets/${crypto.randomUUID()}.${ext}`;

  const { error } = await supabase.storage
    .from('deliverables')
    .upload(path, file, { upsert: false });
  if (error) throw error;

  const { data: urlData } = supabase.storage
    .from('deliverables')
    .getPublicUrl(path);

  // deliverables is private, use signed URL instead
  const { data: signedData, error: signedError } = await supabase.storage
    .from('deliverables')
    .createSignedUrl(path, 60 * 60 * 24 * 365); // 1 year

  const url = signedData?.signedUrl ?? urlData.publicUrl;

  return { name: file.name, url, type: file.type, size: file.size };
}

// ── Get available agents (admins/SA) ──
export function useAvailableAgents() {
  return useQuery({
    queryKey: ['available-agents'],
    queryFn: async () => {
      const { data: roleRows, error } = await supabase
        .from('user_roles')
        .select('user_id, role')
        .in('role', ['super_admin', 'admin']);
      if (error) throw error;
      if (!roleRows || roleRows.length === 0) return [];

      const userIds = [...new Set(roleRows.map(r => r.user_id))];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', userIds);

      return (profiles ?? []).map(p => ({
        id: p.id,
        name: p.full_name,
        email: p.email,
      }));
    },
  });
}

// ── Mark message as solution ──
export function useMarkSolution() {
  const { user } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ ticketId, messageId }: { ticketId: string; messageId: string | null }) => {
      const updates: any = {
        solution_message_id: messageId,
        updated_at: new Date().toISOString(),
      };
      // If marking a solution, also resolve the ticket
      if (messageId) {
        updates.status = 'resolved';
      }
      const { error } = await supabase
        .from('support_tickets')
        .update(updates)
        .eq('id', ticketId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-tickets'] });
      qc.invalidateQueries({ queryKey: ['all-tickets'] });
      if (user) {
        logAuditAction(user.id, 'ticket_mark_solution', 'Mensaje marcado como solución');
      }
    },
  });
}
